import React, { useState } from 'react';
import useSubdomain from '../../hooks/useSubdomain';
import { useTheme } from '../../hooks/useTheme';
import DataLoading from '../../components/dataLoading';
import { useTranslation } from 'react-i18next';
import { getCall, postCall } from '../../hooks/Network';
import { toast } from 'react-toastify';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import { cacheGet } from '../../util/cache';
import ButtonObelisk from '../../components/ui/ButtonComponent';
import AlertBox from '../../components/ui/AlertBox';
import { GrAlert } from 'react-icons/gr';
const ApiKey = process.env.REACT_APP_API_KEY;

// Forgot Password component

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isSended, setIsSended] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [emailAddress, setEmailAddress] = useState('');
  const [otp, setOTP] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [successMsg, setSuccessMsg] = useState('');
  const [msg, setMsg] = useState('');

  const { subdomain } = useSubdomain();
  const { theme, loading, error } = useTheme({ subdomain: subdomain });
  const { t } = useTranslation();

  const themeLogo = `/images/webOffice.png`;
  const loginHeadingColor = '#9e9e9e';

  if (loading) return <DataLoading title='Loading Theme' />;
  if (error) return `Error! ${error}`;

  function validateEmail(email) {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  // Handle change Password function

  const handleChangePassword = async (e) => {
    e.preventDefault();

    try {
      if (email == '') {
        setMsg(t('pleaseEnterTheEmail'));
        return false;
      }

      if (validateEmail(email)) {
        setMsg('');
      } else {
        setMsg(t('invalidEmail'));
        return false;
      }

      setIsLoading(true);
      setIsSended(true);

      let res = await postCall({
        url: 'sendForgotPasswordEmail',
        bodyData: { Email: email },
        header: { 'API-key': ApiKey },
      });

      if (res?.status === 200 && res?.data) {
        toast.success(res?.data?.message);
        setSuccessMsg('Password reset link sent on mail, Please check your mail');
      } else {
        console.log('Error');
        toast.error(t('errorForgetPassword'));
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  if (theme?.favicon?.url) {
    const favicon = document.querySelector('link[rel="icon"]');

    if (favicon) {
      favicon.href = theme?.favicon?.url;
    } else {
      const newFavicon = document.createElement('link');
      newFavicon.rel = 'icon';
      newFavicon.href = theme?.favicon?.url;
      document.head.appendChild(newFavicon);
    }
  }
  const themeMode = cacheGet('setting.theme.mode', false, 'light');

  return (
    <>
      {isLoading && <DataLoading title='Loading' />}

      {!isLoading && (
        <Box
          className={`d-flex page-wrapper page-center justify-content-center align-items-center`}
          sx={{
            background: themeMode == 'dark' ? '#000' : '#333',
            // display: isAlert ? 'none' : 'block',
            margin: { xs: 0 },
            padding: { xs: 0 },
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: { xs: '100vh', sm: 'auto' },
              maxWidth: { xs: 'none', sm: '450px' },
              borderRadius: 0,
              padding: { xs: 0 },
              margin: { xs: 0 },
              paddingRight: { xs: '0px !important' },
              paddingLeft: { xs: '0px !important' },
            }}
            className={themeMode == 'dark' ? 'mui-mode-dark' : 'mui-mode-light'}
          >
            <Card
              className='card card-md'
              component='form'
              autoComplete='off'
              noValidate
              sx={{ p: 2 }}
              onSubmit={handleChangePassword}
            >
              <CardContent className='card-body'>
                <Box className='text-center d-print-none text-white'>
                  <Box
                    component='img'
                    src={theme?.loginLogo?.url ?? themeLogo}
                    sx={{
                      height: { xs: 'auto', sm: '60px' },
                      marginBottom: '0.5rem',
                      marginTop: '0.5rem',
                      width: 'auto',
                      maxWidth: { xs: '150px', sm: '200px' },
                      filter: themeMode == 'dark' ? 'brightness(0) invert(1)' : 'none',
                    }}
                  />
                </Box>

                <h2
                  id='loginHeading'
                  className='h2 text-center mb-2 '
                  style={{ color: loginHeadingColor }}
                >
                  {t('forgotPassword')}
                </h2>

                <Typography variant='body2' className='text-muted mb-4' align='center'>
                  {t('enterYourEmailInstructions')}
                </Typography>

                <Alert
                  id='passwordSuccess'
                  severity='success'
                  className='d-none'
                  sx={{ mb: 2 }}
                >
                  {t('anEmailWithInstructions')}
                </Alert>

                <Box className='mb-4'>
                  <TextField
                    label={t('emailAddress')}
                    type='email'
                    className={`mui-${themeMode == 'dark' ? 'dark' : 'light'}`}
                    placeholder='Enter email'
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Box>

                {msg && (
                  <AlertBox severity='error' useIcon={<GrAlert color='#FFF' size={24} />}>
                    {msg}
                  </AlertBox>
                )}

                <Box
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  className='form-footer'
                  mt={2}
                >
                  <ButtonObelisk m={0} type='submit' disabled={isSended === true}>
                    <Box display='flex' alignItems='center' gap={1}>
                      <MailOutlineIcon />
                      {t('sendMeNewPassword')}
                    </Box>
                  </ButtonObelisk>
                </Box>

                <Typography
                  className='text-center text-muted mt-3'
                  variant='body2'
                  align='center'
                >
                  {t('returnTo')} <a href='/'>{t('signIN')}</a>
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
    </>
  );
};

// Get Os function
const getOS = (userAgent) => {
  const { t } = useTranslation();

  const platformMatch = userAgent.match(/(Win|Mac|Linux)/i);
  if (platformMatch) {
    const platform = platformMatch[0].toLowerCase();
    if (platform === 'win') return t('windows');
    return platform;
  }
  return t('unknown');
};

// Get Browser Info Function
const getBrowserInfo = (userAgent) => {
  const { t } = useTranslation();

  if (userAgent.indexOf('Chrome') !== -1) return t('googleChrome');
  else if (userAgent.indexOf('Safari') !== -1) return t('safari');
  else if (userAgent.indexOf('Firefox') !== -1) return t('mozillaFirefox');
  else if (userAgent.indexOf('Edge') !== -1) return t('microsoftEdge');
  else if (userAgent.indexOf('MSIE') !== -1 || userAgent.indexOf('Trident/') !== -1)
    return t('internetExplorer');

  return t('unknown');
};

export default ForgotPassword;
